import type { ScheduleEvent } from '../api/science/openapi';
import type { UnifiedChatRichContext } from '../types';

/**
 * Chat `result_json` after it has passed the stream-boundary check.
 * Extra fields are kept so a backend can add data without a client release;
 * known fields with the wrong type are rejected instead.
 */
export interface ChatResultAdjustments {
  meals?: Record<string, unknown>[] | null;
  schedule?: ScheduleEvent[] | null;
  todo_list?: Record<string, unknown>[] | null;
  routine_adjustments?: Record<string, unknown>[] | null;
  sleep?: Record<string, unknown> | null;
}

export interface ChatUnifiedResponseEntry {
  date?: string;
  suggestion_id?: string;
  adjustments?: ChatResultAdjustments | null;
  query?: { rich_context?: UnifiedChatRichContext | null } | null;
}

export interface ChatResultJson {
  summary?: string;
  chat_response?: string;
  session_id?: string | null;
  suggestions?: unknown[];
  schedules?: ScheduleEvent[] | null;
  schedule?: Record<string, unknown>[] | null;
  query?: { rich_context?: UnifiedChatRichContext | null };
  unified_chat_responses?: ChatUnifiedResponseEntry[] | null;
  [key: string]: unknown;
}

export interface ChatQuestionPayload {
  options: unknown;
  trigger: unknown;
  [key: string]: unknown;
}

export type ParsedChatStreamResult =
  | { kind: 'question'; question: ChatQuestionPayload }
  | { kind: 'result'; resultJson: ChatResultJson; sessionId?: string };

export class ResultJsonValidationError extends Error {
  readonly issues: string[];

  constructor(issues: string[]) {
    super(`Invalid result_json: ${issues.join('; ')}`);
    this.name = 'ResultJsonValidationError';
    this.issues = issues;
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isAbsent = (value: unknown) => value === undefined || value === null;

const requireString = (issues: string[], path: string, value: unknown) => {
  if (isAbsent(value) || typeof value === 'string') {
    return;
  }
  issues.push(`${path} must be a string`);
};

const requireRecord = (issues: string[], path: string, value: unknown) => {
  if (isAbsent(value) || isRecord(value)) {
    return;
  }
  issues.push(`${path} must be an object`);
};

const requireArray = (issues: string[], path: string, value: unknown) => {
  if (isAbsent(value) || Array.isArray(value)) {
    return;
  }
  issues.push(`${path} must be an array`);
};

const requireArrayOfRecords = (issues: string[], path: string, value: unknown) => {
  if (isAbsent(value)) {
    return;
  }
  if (!Array.isArray(value)) {
    issues.push(`${path} must be an array`);
    return;
  }
  value.forEach((item, index) => {
    if (!isRecord(item)) {
      issues.push(`${path}[${index}] must be an object`);
    }
  });
};

const ADJUSTMENT_ARRAY_FIELDS = ['meals', 'schedule', 'todo_list', 'routine_adjustments'] as const;

const validateAdjustments = (
  adjustments: Record<string, unknown>,
  path: string,
  issues: string[]
) => {
  ADJUSTMENT_ARRAY_FIELDS.forEach((field) => {
    requireArrayOfRecords(issues, `${path}.${field}`, adjustments[field]);
  });
  requireRecord(issues, `${path}.sleep`, adjustments.sleep);
};

const validateResponseEntry = (entry: unknown, index: number, issues: string[]) => {
  const path = `unified_chat_responses[${index}]`;
  if (!isRecord(entry)) {
    issues.push(`${path} must be an object`);
    return;
  }

  requireString(issues, `${path}.date`, entry.date);
  requireString(issues, `${path}.suggestion_id`, entry.suggestion_id);

  if (!isAbsent(entry.adjustments)) {
    if (!isRecord(entry.adjustments)) {
      issues.push(`${path}.adjustments must be an object`);
    } else {
      validateAdjustments(entry.adjustments, `${path}.adjustments`, issues);
    }
  }

  if (isAbsent(entry.query)) {
    return;
  }
  if (!isRecord(entry.query)) {
    issues.push(`${path}.query must be an object`);
    return;
  }
  requireRecord(issues, `${path}.query.rich_context`, entry.query.rich_context);
};

const validateResultJson = (result: Record<string, unknown>, issues: string[]) => {
  requireString(issues, 'summary', result.summary);
  requireString(issues, 'chat_response', result.chat_response);
  requireString(issues, 'session_id', result.session_id);
  requireArray(issues, 'suggestions', result.suggestions);
  requireArrayOfRecords(issues, 'schedules', result.schedules);
  requireArrayOfRecords(issues, 'schedule', result.schedule);

  if (!isAbsent(result.query)) {
    if (!isRecord(result.query)) {
      issues.push('query must be an object');
    } else {
      requireRecord(issues, 'query.rich_context', result.query.rich_context);
    }
  }

  if (isAbsent(result.unified_chat_responses)) {
    return;
  }
  if (!Array.isArray(result.unified_chat_responses)) {
    issues.push('unified_chat_responses must be an array');
    return;
  }
  result.unified_chat_responses.forEach((entry, index) => {
    validateResponseEntry(entry, index, issues);
  });
};

const parseJsonPayload = (raw: unknown): unknown => {
  if (typeof raw !== 'string') {
    return raw;
  }
  try {
    return JSON.parse(raw);
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'parse error';
    throw new ResultJsonValidationError([`payload is not valid JSON (${detail})`]);
  }
};

/**
 * Network-boundary parser for a chat `result_json` SSE payload.
 * Accepts the raw event data (string or already-parsed object). Throws
 * {@link ResultJsonValidationError} when a known field has the wrong type
 * so callers can fail the turn instead of rendering a guessed shape.
 */
export const parseChatStreamResult = (raw: unknown): ParsedChatStreamResult => {
  const parsed = parseJsonPayload(raw);
  if (!isRecord(parsed)) {
    throw new ResultJsonValidationError(['payload must be an object']);
  }

  if (parsed.options && parsed.trigger) {
    return { kind: 'question', question: parsed as ChatQuestionPayload };
  }

  const resultSource = parsed.result_json != null ? parsed.result_json : parsed;
  if (!isRecord(resultSource)) {
    throw new ResultJsonValidationError(['result_json must be an object']);
  }

  const issues: string[] = [];
  validateResultJson(resultSource, issues);
  if (issues.length > 0) {
    throw new ResultJsonValidationError(issues);
  }

  const sessionId = typeof parsed.session_id === 'string' ? parsed.session_id : undefined;
  return { kind: 'result', resultJson: resultSource as ChatResultJson, sessionId };
};
